import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Building2, Briefcase, Linkedin, Calendar, Star, Clock, MessageSquare, Phone, Mail, Video, Edit2, Save, X, Mic, MicOff, Trash2, ChevronRight, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { contactService, aiService } from '../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import API_URL from '../config/API_URL';

const ContactDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [contactData, setContactData] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddInteraction, setShowAddInteraction] = useState(false);
  const [newInteraction, setNewInteraction] = useState({
    type: 'note',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});

  const [additionalNotes, setAdditionalNotes] = useState([]);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');

  // Important dates states
  const [importantDates, setImportantDates] = useState([]);
  const [showAddDate, setShowAddDate] = useState(false);
  const [newDate, setNewDate] = useState({
    date_type: 'custom',
    date_value: '',
    label: '',
    notes: '',
    recurring: true
  });
  const [editingDateId, setEditingDateId] = useState(null);
  const [editingDateData, setEditingDateData] = useState(null);

  // Voice memo states
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [useVoiceInput, setUseVoiceInput] = useState(false);

  // Contact-specific call transcripts
  const [contactTranscripts, setContactTranscripts] = useState([]);
  const [loadingContactTranscripts, setLoadingContactTranscripts] = useState(false);
  const [expandedContactTranscript, setExpandedContactTranscript] = useState(null);

  const styles = {
    page: {
      padding: '2rem',
      maxWidth: '1400px',
      margin: '0 auto',
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.25rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: '#f8fafc',
      color: '#475569',
      marginBottom: '1.5rem',
    },
    header: {
      background: '#ffffff',
      borderRadius: '16px',
      padding: '2rem',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      border: '1px solid #e2e8f0',
      marginBottom: '1.5rem',
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '1.5rem',
    },
    nameSection: {
      flex: 1,
    },
    name: {
      fontSize: '2rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 800,
      color: '#0f172a',
      marginBottom: '0.5rem',
    },
    role: {
      fontSize: '1.1rem',
      color: '#64748b',
      marginBottom: '0.25rem',
    },
    company: {
      fontSize: '1rem',
      color: '#94a3b8',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    importanceBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      borderRadius: '8px',
      fontSize: '0.875rem',
      fontWeight: 600,
      fontFamily: "'Syne', sans-serif",
    },
    detailsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1rem',
    },
    detailItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '12px',
    },
    detailIcon: {
      color: '#667eea',
    },
    detailContent: {
      flex: 1,
    },
    detailLabel: {
      fontSize: '0.85rem',
      color: '#64748b',
      marginBottom: '0.25rem',
    },
    detailValue: {
      fontSize: '0.95rem',
      color: '#0f172a',
      fontWeight: 500,
    },
    contentGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1.5rem',
    },
    section: {
      background: '#ffffff',
      borderRadius: '16px',
      padding: '2rem',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      border: '1px solid #e2e8f0',
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
    },
    sectionTitle: {
      fontSize: '1.25rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      color: '#0f172a',
    },
    addButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '8px',
      fontSize: '0.875rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
    },
    infoBox: {
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '12px',
      color: '#475569',
      fontSize: '0.95rem',
      lineHeight: '1.6',
      marginBottom: '1rem',
    },
    interactionsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    interactionCard: {
      padding: '1.25rem',
      background: '#f8fafc',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
    },
    interactionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.75rem',
    },
    interactionType: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.9rem',
      fontWeight: 600,
      color: '#0f172a',
    },
    interactionDate: {
      fontSize: '0.85rem',
      color: '#64748b',
    },
    interactionNotes: {
      fontSize: '0.95rem',
      color: '#475569',
      lineHeight: '1.6',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      marginTop: '1rem',
      padding: '1.5rem',
      background: '#f8fafc',
      borderRadius: '12px',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    label: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: '#0f172a',
    },
    select: {
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#ffffff',
      outline: 'none',
      cursor: 'pointer',
    },
    textarea: {
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#ffffff',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.5rem',
    },
    submitButton: {
      flex: 1,
      padding: '0.75rem 1.25rem',
      border: 'none',
      borderRadius: '8px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
    },
    cancelButton: {
      flex: 1,
      padding: '0.75rem 1.25rem',
      border: 'none',
      borderRadius: '8px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: '#64748b',
      color: 'white',
    },
    emptyState: {
      textAlign: 'center',
      padding: '2rem',
      color: '#64748b',
    },
    alert: {
      padding: '0.875rem',
      borderRadius: '12px',
      marginBottom: '1rem',
      fontSize: '0.95rem',
    },
    loading: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#475569',
      fontSize: '1.1rem',
    },
    editButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '8px',
      fontSize: '0.875rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: '#3b82f6',
      color: 'white',
    },
    editForm: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    editFormRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      background: '#ffffff',
      outline: 'none',
      boxSizing: 'border-box',
    },
    voiceToggle: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1rem',
      background: '#f8fafc',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 200ms',
      marginBottom: '1rem',
    },
    voiceToggleActive: {
      background: '#eff6ff',
      borderColor: '#3b82f6',
    },
    voiceMemoContainer: {
      padding: '1.5rem',
      background: '#f8fafc',
      border: '2px dashed #cbd5e1',
      borderRadius: '12px',
      marginBottom: '1rem',
    },
    recordButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '1rem 1.5rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      width: '100%',
      marginBottom: '1rem',
    },
    recordingIndicator: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem',
      background: '#fee2e2',
      borderRadius: '8px',
      color: '#dc2626',
      fontSize: '0.9rem',
      fontWeight: 500,
      marginBottom: '1rem',
    },
    recordingDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: '#dc2626',
      animation: 'pulse 1.5s ease-in-out infinite',
    },
    transcriptBox: {
      padding: '1rem',
      background: '#ffffff',
      borderRadius: '8px',
      fontSize: '0.9rem',
      color: '#475569',
      maxHeight: '150px',
      overflowY: 'auto',
      marginBottom: '1rem',
    },
    processButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.25rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      color: 'white',
      width: '100%',
    },
    dateCard: {
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      marginBottom: '0.75rem',
    },
    dateHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '0.5rem',
    },
    dateType: {
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      background: '#dbeafe',
      color: '#1e40af',
      borderRadius: '6px',
      fontSize: '0.8rem',
      fontWeight: 600,
      marginBottom: '0.5rem',
    },
    dateValue: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#0f172a',
      marginBottom: '0.25rem',
    },
    dateLabel: {
      fontSize: '0.9rem',
      color: '#64748b',
      marginBottom: '0.25rem',
    },
    dateActions: {
      display: 'flex',
      gap: '0.5rem',
    },
    iconButton: {
      padding: '0.25rem 0.5rem',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '0.85rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      transition: 'all 200ms',
    },
    transcriptItem: {
      background: '#f8fafc',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      padding: '1.25rem',
      marginBottom: '1rem',
      cursor: 'pointer',
      transition: 'all 200ms',
    },
    transcriptItemExpanded: {
      background: '#ffffff',
      borderColor: '#667eea',
    },
    transcriptHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '0.5rem',
    },
    transcriptDate: {
      fontSize: '0.85rem',
      color: '#64748b',
    },
    transcriptSummaryPreview: {
      fontSize: '0.9rem',
      color: '#64748b',
      lineHeight: 1.6,
      marginTop: '0.75rem',
    },
    transcriptExpandedContent: {
      marginTop: '1.5rem',
      paddingTop: '1.5rem',
      borderTop: '2px solid #e2e8f0',
    },
    transcriptSection: {
      marginBottom: '1.5rem',
    },
    transcriptSectionTitle: {
      fontSize: '0.95rem',
      fontWeight: 700,
      color: '#0f172a',
      marginBottom: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    transcriptFullText: {
      fontSize: '0.9rem',
      lineHeight: 1.7,
      color: '#475569',
      whiteSpace: 'pre-wrap',
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      maxHeight: '300px',
      overflowY: 'auto',
    },
    keywordsList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '0.75rem',
    },
    keyword: {
      padding: '0.5rem 1rem',
      background: '#eff6ff',
      color: '#1e40af',
      borderRadius: '8px',
      fontSize: '0.85rem',
      fontWeight: 600,
    },
  };

  useEffect(() => {
    if (id) {
      loadContactDetail();
      loadImportantDates();
      loadContactTranscripts();
    }
  }, [id]);

  const loadContactDetail = async () => {
    try {
      const data = await contactService.getContact(id);
      setContactData(data.contact);
      setInteractions(data.contact.interactions || []);
      setAdditionalNotes(data.contact.additional_notes || []);
    } catch (error) {
      console.error('Failed to load contact details:', error);
      setError('Failed to load contact details');
    } finally {
      setLoading(false);
    }
  };

  const loadImportantDates = async () => {
    try {
      const data = await contactService.getImportantDates(id);
      setImportantDates(data.dates || []);
    } catch (error) {
      console.error('Failed to load important dates:', error);
    }
  };

  const loadContactTranscripts = async () => {
    setLoadingContactTranscripts(true);
    try {
      const response = await fetch(`${API_URL}/api/call-transcripts?contact_id=${id}`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to load transcripts');
      }
      
      const data = await response.json();
      setContactTranscripts(data.transcripts || []);
    } catch (error) {
      console.error('Failed to load contact transcripts:', error);
    } finally {
      setLoadingContactTranscripts(false);
    }
  };

  const loadFullContactTranscript = async (transcriptId) => {
    try {
      const response = await fetch(`${API_URL}/api/call-transcripts/${transcriptId}`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to load transcript details');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to load transcript details:', error);
      setError('Failed to load transcript details');
      return null;
    }
  };

  const handleContactTranscriptClick = async (transcript) => {
    if (expandedContactTranscript?.id === transcript.id) {
      setExpandedContactTranscript(null);
    } else {
      const fullData = await loadFullContactTranscript(transcript.id);
      if (fullData) {
        setExpandedContactTranscript(fullData);
      }
    }
  };

  const handleAddDate = async (e) => {
    e.preventDefault();
    if (!newDate.date_value) {
      setError('Please enter a date');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const result = await contactService.addImportantDate(id, newDate);
      setImportantDates([result.date, ...importantDates]);
      setNewDate({
        date_type: 'custom',
        date_value: '',
        label: '',
        notes: '',
        recurring: true
      });
      setShowAddDate(false);
      setSuccess('Important date added successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to add important date. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateDate = async (dateId) => {
    if (!editingDateData.date_value) {
      setError('Date cannot be empty');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await contactService.updateImportantDate(id, dateId, editingDateData);
      setImportantDates(importantDates.map(date => 
        date.id === dateId ? { ...date, ...editingDateData } : date
      ));
      setEditingDateId(null);
      setEditingDateData(null);
      setSuccess('Important date updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update important date. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDate = async (dateId) => {
    if (!window.confirm('Are you sure you want to delete this important date?')) return;

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await contactService.deleteImportantDate(id, dateId);
      setImportantDates(importantDates.filter(date => date.id !== dateId));
      setSuccess('Important date deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete important date. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVoiceRecording = async () => {
    if (isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream);
        const chunks = [];

        recorder.ondataavailable = (event) => {
          chunks.push(event.data);
        };

        recorder.onstop = async () => {
          const audioBlob = new Blob(chunks, { type: 'audio/webm' });
          setIsProcessingAudio(true);
          
          try {
            const result = await aiService.transcribeAndExtractInteraction(audioBlob);
            setVoiceTranscript(result.transcript);
            
            if (result.extracted_data) {
              const data = JSON.parse(result.extracted_data);
              setNewInteraction({
                type: data.interaction_type || 'note',
                notes: data.notes || result.transcript,
              });
              setSuccess('Voice memo processed! Review and submit below.');
            }
          } catch (error) {
            console.error('Failed to process audio:', error);
            setError('Failed to process audio. Please try again.');
          } finally {
            setIsProcessingAudio(false);
          }
        };

        recorder.start();
        setMediaRecorder(recorder);
        setIsRecording(true);
      } catch (error) {
        console.error('Failed to start recording:', error);
        setError('Failed to access microphone. Please check your permissions.');
      }
    }
  };

  const clearVoiceInput = () => {
    setVoiceTranscript('');
    setNewInteraction({ type: 'note', notes: '' });
    setUseVoiceInput(false);
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) {
      setError('Please enter a note');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const result = await contactService.addNote(id, newNote);
      setAdditionalNotes([result.note, ...additionalNotes]);
      setNewNote('');
      setShowAddNote(false);
      setSuccess('Note added successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to add note. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditNote = async (noteId) => {
    if (!editingNoteContent.trim()) {
      setError('Note cannot be empty');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const result = await contactService.updateNote(id, noteId, editingNoteContent);
      setAdditionalNotes(additionalNotes.map(note => 
        note.id === noteId ? result.note : note
      ));
      setEditingNoteId(null);
      setEditingNoteContent('');
      setSuccess('Note updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update note. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await contactService.deleteNote(id, noteId);
      setAdditionalNotes(additionalNotes.filter(note => note.id !== noteId));
      setSuccess('Note deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete note. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddInteraction = async (e) => {
    e.preventDefault();
    if (!newInteraction.notes.trim()) {
      setError('Please enter interaction notes');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const result = await contactService.addInteraction(id, newInteraction);
      setInteractions([result.interaction, ...interactions]);
      setNewInteraction({ type: 'note', notes: '' });
      setShowAddInteraction(false);
      setUseVoiceInput(false);
      setVoiceTranscript('');
      setSuccess('Interaction added successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to add interaction. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await contactService.updateContact(id, editFormData);
      setContactData({ ...contactData, ...editFormData });
      setIsEditing(false);
      setSuccess('Contact updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to update contact. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getImportanceStyle = (importance) => {
    const styleMap = {
      low: { background: '#e0f2fe', color: '#0369a1' },
      medium: { background: '#dbeafe', color: '#1e40af' },
      high: { background: '#fef3c7', color: '#92400e' },
      critical: { background: '#fee2e2', color: '#991b1b' },
    };
    return styleMap[importance] || styleMap.medium;
  };

  const getInteractionIcon = (type) => {
    const icons = {
      meeting: <Video size={16} />,
      email: <Mail size={16} />,
      message: <MessageSquare size={16} />,
      call: <Phone size={16} />,
      note: <MessageSquare size={16} />,
    };
    return icons[type] || icons.note;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric'
    });
  };

  const getDateTypeName = (type) => {
    const types = {
      birthday: 'Birthday',
      anniversary: 'Anniversary',
      work_anniversary: 'Work Anniversary',
      custom: 'Custom Date'
    };
    return types[type] || 'Custom Date';
  };

  const handleEdit = () => {
    setEditFormData({
      name: contactData.name,
      company: contactData.company || '',
      role: contactData.role || '',
      linkedin_url: contactData.linkedin_url || '',
      connection_origin: contactData.connection_origin || '',
      interests: contactData.interests || '',
      conversation_notes: contactData.conversation_notes || '',
      birthday: contactData.birthday || '',
      contact_importance: contactData.contact_importance || 'medium',
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditFormData({});
    setError('');
  };

  if (loading) {
    return <div style={styles.loading}>Loading contact details...</div>;
  }

  if (!contactData) {
    return <div style={styles.loading}>Contact not found</div>;
  }

  return (
    <div style={styles.page}>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
        `}
      </style>

      <button
        style={styles.backButton}
        onClick={() => navigate('/dashboard/contacts')}
        onMouseEnter={(e) => e.target.style.background = '#e2e8f0'}
        onMouseLeave={(e) => e.target.style.background = '#f8fafc'}
      >
        <ArrowLeft size={20} />
        Back to Contacts
      </button>

      {success && (
        <div style={{ ...styles.alert, background: '#d1fae5', color: '#065f46', borderLeft: '4px solid #10b981' }}>
          {success}
        </div>
      )}

      {error && (
        <div style={{ ...styles.alert, background: '#fee2e2', color: '#dc2626', borderLeft: '4px solid #dc2626' }}>
          {error}
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={styles.header}
      >
        <div style={styles.headerTop}>
          <div style={styles.nameSection}>
            <h1 style={styles.name}>{contactData.name}</h1>
            {contactData.role && (
              <div style={styles.role}>
                <Briefcase size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />
                {contactData.role}
              </div>
            )}
            {contactData.company && (
              <div style={styles.company}>
                <Building2 size={16} />
                {contactData.company}
              </div>
            )}
          </div>
          {contactData.contact_importance && (
            <div style={{ ...styles.importanceBadge, ...getImportanceStyle(contactData.contact_importance) }}>
              <Star size={16} />
              {contactData.contact_importance.charAt(0).toUpperCase() + contactData.contact_importance.slice(1)} Priority
            </div>
          )}
        </div>

        <div style={styles.detailsGrid}>
          {contactData.connection_origin && (
            <div style={styles.detailItem}>
              <div style={styles.detailIcon}>
                <Calendar size={20} />
              </div>
              <div style={styles.detailContent}>
                <div style={styles.detailLabel}>How you connected</div>
                <div style={styles.detailValue}>{contactData.connection_origin}</div>
              </div>
            </div>
          )}

          {contactData.linkedin_url && (
            <div style={styles.detailItem}>
              <div style={styles.detailIcon}>
                <Linkedin size={20} />
              </div>
              <div style={styles.detailContent}>
                <div style={styles.detailLabel}>LinkedIn</div>
                <a 
                  href={contactData.linkedin_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ ...styles.detailValue, color: '#667eea', textDecoration: 'none' }}
                >
                  View Profile
                </a>
              </div>
            </div>
          )}

          {contactData.last_contacted && (
            <div style={styles.detailItem}>
              <div style={styles.detailIcon}>
                <Clock size={20} />
              </div>
              <div style={styles.detailContent}>
                <div style={styles.detailLabel}>Last contacted</div>
                <div style={styles.detailValue}>{formatDate(contactData.last_contacted)}</div>
              </div>
            </div>
          )}

          <div style={styles.detailItem}>
            <div style={styles.detailIcon}>
              <MessageSquare size={20} />
            </div>
            <div style={styles.detailContent}>
              <div style={styles.detailLabel}>Total interactions</div>
              <div style={styles.detailValue}>{interactions.length}</div>
            </div>
          </div>
        </div>
      </motion.div>

      <div style={styles.contentGrid}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={styles.section}
        >
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Contact Information</h2>
            {!isEditing && (
              <button
                style={styles.editButton}
                onClick={handleEdit}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                <Edit2 size={16} />
                Edit Contact
              </button>
            )}
          </div>
          
          {isEditing ? (
            <form onSubmit={handleSaveEdit} style={styles.editForm}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Name *</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  style={styles.input}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  required
                />
              </div>

              <div style={styles.editFormRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Company</label>
                  <input
                    type="text"
                    value={editFormData.company}
                    onChange={(e) => setEditFormData({ ...editFormData, company: e.target.value })}
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Role</label>
                  <input
                    type="text"
                    value={editFormData.role}
                    onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })}
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>LinkedIn URL</label>
                <input
                  type="url"
                  value={editFormData.linkedin_url}
                  onChange={(e) => setEditFormData({ ...editFormData, linkedin_url: e.target.value })}
                  style={styles.input}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>How You Connected</label>
                <input
                  type="text"
                  value={editFormData.connection_origin}
                  onChange={(e) => setEditFormData({ ...editFormData, connection_origin: e.target.value })}
                  style={styles.input}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Interests</label>
                <input
                  type="text"
                  value={editFormData.interests}
                  onChange={(e) => setEditFormData({ ...editFormData, interests: e.target.value })}
                  style={styles.input}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>

              <div style={styles.editFormRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Birthday</label>
                  <input
                    type="date"
                    value={editFormData.birthday}
                    onChange={(e) => setEditFormData({ ...editFormData, birthday: e.target.value })}
                    style={styles.input}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Contact Importance</label>
                  <select
                    value={editFormData.contact_importance}
                    onChange={(e) => setEditFormData({ ...editFormData, contact_importance: e.target.value })}
                    style={styles.select}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  >
                    <option value="low">Low Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="high">High Priority</option>
                    <option value="critical">Critical Contact</option>
                  </select>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Conversation Notes</label>
                <textarea
                  value={editFormData.conversation_notes}
                  onChange={(e) => setEditFormData({ ...editFormData, conversation_notes: e.target.value })}
                  style={styles.textarea}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>

              <div style={styles.buttonGroup}>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{ ...styles.submitButton, opacity: submitting ? 0.5 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
                  onMouseEnter={(e) => { if (!submitting) e.target.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { if (!submitting) e.target.style.transform = 'translateY(0)'; }}
                >
                  <Save size={16} />
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  style={styles.cancelButton}
                  onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  <X size={16} />
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <>
              {contactData.interests && (
                <div style={styles.infoBox}>
                  <strong style={{ color: '#0f172a', display: 'block', marginBottom: '0.5rem' }}>Interests:</strong>
                  {contactData.interests}
                </div>
              )}

              {contactData.conversation_notes && (
                <div style={styles.infoBox}>
                  <strong style={{ color: '#0f172a', display: 'block', marginBottom: '0.5rem' }}>Initial Conversation:</strong>
                  {contactData.conversation_notes}
                </div>
              )}

              {contactData.birthday && (
                <div style={styles.infoBox}>
                  <strong style={{ color: '#0f172a', display: 'block', marginBottom: '0.5rem' }}>Birthday:</strong>
                  {formatDateOnly(contactData.birthday)}
                </div>
              )}
            </>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={styles.section}
        >
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Interactions</h2>
            <button
              style={styles.addButton}
              onClick={() => setShowAddInteraction(!showAddInteraction)}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              <Plus size={16} />
              Add Interaction
            </button>
          </div>

          {showAddInteraction && (
            <div style={styles.form}>
              <div
                onClick={() => setUseVoiceInput(!useVoiceInput)}
                style={{
                  ...styles.voiceToggle,
                  ...(useVoiceInput ? styles.voiceToggleActive : {})
                }}
              >
                <Mic size={18} />
                <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                  {useVoiceInput ? 'Using Voice Input' : 'Use Voice Input'}
                </span>
              </div>

              {useVoiceInput && (
                <div style={styles.voiceMemoContainer}>
                  {isRecording && (
                    <div style={styles.recordingIndicator}>
                      <div style={styles.recordingDot}></div>
                      Recording... Speak naturally about your interaction
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleVoiceRecording}
                    disabled={isProcessingAudio}
                    style={{
                      ...styles.recordButton,
                      background: isRecording ? '#ef4444' : '#10b981',
                      color: 'white',
                      boxShadow: isRecording 
                        ? '0 4px 12px rgba(239, 68, 68, 0.4)' 
                        : '0 4px 12px rgba(16, 185, 129, 0.4)',
                      opacity: isProcessingAudio ? 0.5 : 1,
                      cursor: isProcessingAudio ? 'not-allowed' : 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      if (!isProcessingAudio) {
                        e.target.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isProcessingAudio) {
                        e.target.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    {isRecording ? <MicOff size={20} /> : <Mic size={20} />}
                    {isProcessingAudio 
                      ? 'Processing...' 
                      : isRecording 
                        ? 'Stop Recording' 
                        : 'Start Recording'}
                  </button>

                  {voiceTranscript && (
                    <>
                      <div style={styles.transcriptBox}>
                        <strong>Transcript:</strong> {voiceTranscript}
                      </div>
                      <button
                        type="button"
                        onClick={clearVoiceInput}
                        style={{
                          ...styles.cancelButton,
                          width: '100%',
                        }}
                        onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                      >
                        Clear Voice Input
                      </button>
                    </>
                  )}

                  <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '1rem', lineHeight: 1.5 }}>
                    💡 Try: "I just had a call with {contactData.name}. We discussed their new project and they mentioned they're looking for recommendations on project management tools. Follow up next week."
                  </p>
                </div>
              )}

              <form onSubmit={handleAddInteraction}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Interaction Type</label>
                  <select
                    value={newInteraction.type}
                    onChange={(e) => setNewInteraction({ ...newInteraction, type: e.target.value })}
                    style={styles.select}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  >
                    <option value="note">Note</option>
                    <option value="meeting">Meeting</option>
                    <option value="email">Email</option>
                    <option value="message">Message</option>
                    <option value="call">Call</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Notes</label>
                  <textarea
                    value={newInteraction.notes}
                    onChange={(e) => setNewInteraction({ ...newInteraction, notes: e.target.value })}
                    placeholder="What happened during this interaction?"
                    style={styles.textarea}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    required
                  />
                </div>

                <div style={styles.buttonGroup}>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{ ...styles.submitButton, opacity: submitting ? 0.5 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
                    onMouseEnter={(e) => { if (!submitting) e.target.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={(e) => { if (!submitting) e.target.style.transform = 'translateY(0)'; }}
                  >
                    {submitting ? 'Adding...' : 'Add Interaction'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddInteraction(false);
                      setNewInteraction({ type: 'note', notes: '' });
                      setUseVoiceInput(false);
                      setVoiceTranscript('');
                      setError('');
                    }}
                    style={styles.cancelButton}
                    onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          <div style={styles.interactionsList}>
            {interactions.length === 0 ? (
              <div style={styles.emptyState}>
                <p>No interactions yet. Add your first interaction to keep track of your conversations!</p>
              </div>
            ) : (
              interactions.map((interaction) => (
                <motion.div
                  key={interaction.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  style={styles.interactionCard}
                >
                  <div style={styles.interactionHeader}>
                    <div style={styles.interactionType}>
                      {getInteractionIcon(interaction.type)}
                      {interaction.type.charAt(0).toUpperCase() + interaction.type.slice(1)}
                    </div>
                    <div style={styles.interactionDate}>
                      {formatDate(interaction.created_at)}
                    </div>
                  </div>
                  <div style={styles.interactionNotes}>
                    {interaction.notes}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </motion.div>
      </div>

      {/* Call Transcripts Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        style={{ ...styles.section, marginTop: '1.5rem' }}
      >
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Call Transcripts</h2>
          <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
            {contactTranscripts.length} {contactTranscripts.length === 1 ? 'call' : 'calls'} recorded
          </div>
        </div>

        {loadingContactTranscripts ? (
          <div style={styles.emptyState}>
            <p>Loading call transcripts...</p>
          </div>
        ) : contactTranscripts.length === 0 ? (
          <div style={styles.emptyState}>
            <Phone size={48} style={{ color: '#cbd5e1', marginBottom: '1rem' }} />
            <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
              No call transcripts yet
            </div>
            <div style={{ fontSize: '0.9rem' }}>
              Use the AI Call Transcriber to record and transcribe your networking calls with {contactData.name}
            </div>
          </div>
        ) : (
          <div>
            {contactTranscripts.map((transcript) => (
              <div
                key={transcript.id}
                onClick={() => handleContactTranscriptClick(transcript)}
                style={{
                  ...styles.transcriptItem,
                  ...(expandedContactTranscript?.id === transcript.id ? styles.transcriptItemExpanded : {}),
                }}
                onMouseEnter={(e) => {
                  if (expandedContactTranscript?.id !== transcript.id) {
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (expandedContactTranscript?.id !== transcript.id) {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }
                }}
              >
                <div style={styles.transcriptHeader}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.25rem' }}>
                      Call Transcript
                    </div>
                    <div style={styles.transcriptDate}>
                      {formatDate(transcript.recorded_at)} • {Math.floor(transcript.duration_seconds / 60)}m {transcript.duration_seconds % 60}s
                    </div>
                  </div>
                  <motion.div
                    animate={{ rotate: expandedContactTranscript?.id === transcript.id ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronRight size={20} color="#667eea" />
                  </motion.div>
                </div>

                {expandedContactTranscript?.id !== transcript.id && (
                  <div style={styles.transcriptSummaryPreview}>
                    {transcript.summary?.substring(0, 150)}
                    {transcript.summary?.length > 150 ? '...' : ''}
                  </div>
                )}

                {expandedContactTranscript?.id === transcript.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    style={styles.transcriptExpandedContent}
                  >
                    {/* Summary */}
                    <div style={styles.transcriptSection}>
                      <div style={styles.transcriptSectionTitle}>
                        <Sparkles size={18} />
                        Summary
                      </div>
                      <div style={{ fontSize: '0.95rem', lineHeight: 1.7, color: '#475569' }}>
                        {expandedContactTranscript.summary}
                      </div>
                    </div>

                    {/* Keywords */}
                    {expandedContactTranscript.keywords && expandedContactTranscript.keywords.length > 0 && (
                      <div style={styles.transcriptSection}>
                        <div style={styles.transcriptSectionTitle}>Key Topics</div>
                        <div style={styles.keywordsList}>
                          {expandedContactTranscript.keywords.map((keyword, idx) => (
                            <span key={idx} style={styles.keyword}>
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Full Transcript */}
                    <div style={styles.transcriptSection}>
                      <div style={styles.transcriptSectionTitle}>
                        <MessageSquare size={18} />
                        Full Transcript
                      </div>
                      <div style={styles.transcriptFullText}>
                        {expandedContactTranscript.transcript}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Important Dates Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        style={{ ...styles.section, marginTop: '1.5rem' }}
      >
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Important Dates</h2>
          <button
            style={styles.addButton}
            onClick={() => setShowAddDate(!showAddDate)}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <Plus size={16} />
            Add Date
          </button>
        </div>

        {showAddDate && (
          <form onSubmit={handleAddDate} style={styles.form}>
            <div style={styles.editFormRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Date Type</label>
                <select
                  value={newDate.date_type}
                  onChange={(e) => setNewDate({ ...newDate, date_type: e.target.value })}
                  style={styles.select}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                >
                  <option value="birthday">Birthday</option>
                  <option value="anniversary">Anniversary</option>
                  <option value="work_anniversary">Work Anniversary</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Date *</label>
                <input
                  type="date"
                  value={newDate.date_value}
                  onChange={(e) => setNewDate({ ...newDate, date_value: e.target.value })}
                  style={styles.input}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  required
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Label</label>
              <input
                type="text"
                value={newDate.label}
                onChange={(e) => setNewDate({ ...newDate, label: e.target.value })}
                placeholder="e.g., 'Wedding Anniversary'"
                style={styles.input}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Notes</label>
              <textarea
                value={newDate.notes}
                onChange={(e) => setNewDate({ ...newDate, notes: e.target.value })}
                placeholder="Additional notes about this date..."
                style={styles.textarea}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <input
                type="checkbox"
                checked={newDate.recurring}
                onChange={(e) => setNewDate({ ...newDate, recurring: e.target.checked })}
                id="newDateRecurring"
              />
              <label htmlFor="newDateRecurring" style={{ fontSize: '0.9rem', color: '#0f172a', cursor: 'pointer' }}>
                Recurring annually
              </label>
            </div>

            <div style={styles.buttonGroup}>
              <button
                type="submit"
                disabled={submitting}
                style={{ ...styles.submitButton, opacity: submitting ? 0.5 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
                onMouseEnter={(e) => { if (!submitting) e.target.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { if (!submitting) e.target.style.transform = 'translateY(0)'; }}
              >
                {submitting ? 'Adding...' : 'Add Date'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddDate(false);
                  setNewDate({
                    date_type: 'custom',
                    date_value: '',
                    label: '',
                    notes: '',
                    recurring: true
                  });
                  setError('');
                }}
                style={styles.cancelButton}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div style={styles.interactionsList}>
          {importantDates.length === 0 ? (
            <div style={styles.emptyState}>
              <p>No important dates yet. Add birthdays, anniversaries, or other significant dates!</p>
            </div>
          ) : (
            importantDates.map((date) => (
              <motion.div
                key={date.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                style={styles.dateCard}
              >
                {editingDateId === date.id ? (
                  <div>
                    <div style={styles.editFormRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Date Type</label>
                        <select
                          value={editingDateData.date_type}
                          onChange={(e) => setEditingDateData({ ...editingDateData, date_type: e.target.value })}
                          style={styles.select}
                          onFocus={(e) => e.target.style.borderColor = '#667eea'}
                          onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        >
                          <option value="birthday">Birthday</option>
                          <option value="anniversary">Anniversary</option>
                          <option value="work_anniversary">Work Anniversary</option>
                          <option value="custom">Custom</option>
                        </select>
                      </div>

                      <div style={styles.formGroup}>
                        <label style={styles.label}>Date</label>
                        <input
                          type="date"
                          value={editingDateData.date_value}
                          onChange={(e) => setEditingDateData({ ...editingDateData, date_value: e.target.value })}
                          style={styles.input}
                          onFocus={(e) => e.target.style.borderColor = '#667eea'}
                          onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        />
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Label</label>
                      <input
                        type="text"
                        value={editingDateData.label}
                        onChange={(e) => setEditingDateData({ ...editingDateData, label: e.target.value })}
                        style={styles.input}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Notes</label>
                      <textarea
                        value={editingDateData.notes || ''}
                        onChange={(e) => setEditingDateData({ ...editingDateData, notes: e.target.value })}
                        style={styles.textarea}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                      />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                      <input
                        type="checkbox"
                        checked={editingDateData.recurring}
                        onChange={(e) => setEditingDateData({ ...editingDateData, recurring: e.target.checked })}
                        id={`editRecurring${date.id}`}
                      />
                      <label htmlFor={`editRecurring${date.id}`} style={{ fontSize: '0.9rem', color: '#0f172a', cursor: 'pointer' }}>
                        Recurring annually
                      </label>
                    </div>

                    <div style={styles.buttonGroup}>
                      <button
                        onClick={() => handleUpdateDate(date.id)}
                        disabled={submitting}
                        style={{ 
                          ...styles.submitButton,
                          opacity: submitting ? 0.5 : 1,
                          cursor: submitting ? 'not-allowed' : 'pointer',
                        }}
                        onMouseEnter={(e) => { if (!submitting) e.target.style.transform = 'translateY(-2px)'; }}
                        onMouseLeave={(e) => { if (!submitting) e.target.style.transform = 'translateY(0)'; }}
                      >
                        <Save size={16} />
                        {submitting ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => {
                          setEditingDateId(null);
                          setEditingDateData(null);
                        }}
                        style={styles.cancelButton}
                        onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={styles.dateHeader}>
                      <div>
                        <div style={styles.dateType}>
                          {getDateTypeName(date.date_type)}
                        </div>
                        <div style={styles.dateValue}>
                          {formatDateOnly(date.date_value)}
                          {date.recurring && ' (Annual)'}
                        </div>
                        {date.label && (
                          <div style={styles.dateLabel}>
                            {date.label}
                          </div>
                        )}
                      </div>
                      <div style={styles.dateActions}>
                        <button
                          onClick={() => {
                            setEditingDateId(date.id);
                            setEditingDateData({
                              date_type: date.date_type,
                              date_value: date.date_value,
                              label: date.label || '',
                              notes: date.notes || '',
                              recurring: date.recurring
                            });
                          }}
                          style={{
                            ...styles.iconButton,
                            background: '#3b82f6',
                            color: 'white',
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#2563eb'}
                          onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteDate(date.id)}
                          style={{
                            ...styles.iconButton,
                            background: '#ef4444',
                            color: 'white',
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                          onMouseLeave={(e) => e.target.style.background = '#ef4444'}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    {date.notes && (
                      <div style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#64748b' }}>
                        {date.notes}
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            ))
          )}
        </div>
      </motion.div>

      {/* Additional Notes Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        style={{ ...styles.section, marginTop: '1.5rem' }}
      >
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Additional Notes</h2>
          <button
            style={styles.addButton}
            onClick={() => setShowAddNote(!showAddNote)}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <Plus size={16} />
            Add Note
          </button>
        </div>

        {showAddNote && (
          <form onSubmit={handleAddNote} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Note</label>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add any additional information about this contact..."
                style={styles.textarea}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                required
              />
            </div>

            <div style={styles.buttonGroup}>
              <button
                type="submit"
                disabled={submitting}
                style={{ ...styles.submitButton, opacity: submitting ? 0.5 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
                onMouseEnter={(e) => { if (!submitting) e.target.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { if (!submitting) e.target.style.transform = 'translateY(0)'; }}
              >
                {submitting ? 'Adding...' : 'Add Note'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddNote(false);
                  setNewNote('');
                  setError('');
                }}
                style={styles.cancelButton}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div style={styles.interactionsList}>
          {additionalNotes.length === 0 ? (
            <div style={styles.emptyState}>
              <p>No additional notes yet. Add notes to keep track of important details!</p>
            </div>
          ) : (
            additionalNotes.map((note) => (
              <motion.div
                key={note.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                style={styles.interactionCard}
              >
                <div style={styles.interactionHeader}>
                  <div style={styles.interactionDate}>
                    {formatDate(note.created_at)}
                    {note.updated_at !== note.created_at && ' (edited)'}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => {
                        setEditingNoteId(note.id);
                        setEditingNoteContent(note.content);
                      }}
                      style={{
                        ...styles.iconButton,
                        background: '#3b82f6',
                        color: 'white',
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#2563eb'}
                      onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      style={{
                        ...styles.iconButton,
                        background: '#ef4444',
                        color: 'white',
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                      onMouseLeave={(e) => e.target.style.background = '#ef4444'}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
                
                {editingNoteId === note.id ? (
                  <div style={{ marginTop: '0.75rem' }}>
                    <textarea
                      value={editingNoteContent}
                      onChange={(e) => setEditingNoteContent(e.target.value)}
                      style={styles.textarea}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                    <div style={{ ...styles.buttonGroup, marginTop: '0.5rem' }}>
                      <button
                        onClick={() => handleEditNote(note.id)}
                        disabled={submitting}
                        style={{ 
                          ...styles.submitButton, 
                          opacity: submitting ? 0.5 : 1,
                          cursor: submitting ? 'not-allowed' : 'pointer',
                        }}
                        onMouseEnter={(e) => { if (!submitting) e.target.style.transform = 'translateY(-2px)'; }}
                        onMouseLeave={(e) => { if (!submitting) e.target.style.transform = 'translateY(0)'; }}
                      >
                        <Save size={16} />
                        {submitting ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => {
                          setEditingNoteId(null);
                          setEditingNoteContent('');
                        }}
                        style={styles.cancelButton}
                        onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={styles.interactionNotes}>
                    {note.content}
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ContactDetail;